/**
 * The character pill (2.H deliverable 8, `header.pills.character`): a router link to the PC sheet,
 * "Character sheet · Level <n>" for the primary sheet, or "Character sheet" on a `pc` page without
 * one (the sheet's empty state offers the import). Nothing unless `sheets` is on and the document
 * is a live canon document whose type has `has_sheet`. A route link: a plain click navigates.
 */
import { flagsFromMe, type SlotComponentProps } from '@codex/contracts';
import { Link } from '@tanstack/react-router';
import { useShellSession } from '../../shell/session.js';
import { useCharacterDoc, useCharacterSheets } from './data/hooks.js';
import { characterLevel } from './model/level.js';

export function CharacterPill({ docId }: SlotComponentProps) {
  const me = useShellSession();
  const on = me !== null && flagsFromMe(me).sheets;
  const cd = useCharacterDoc(docId, on);
  const shown = on && cd.sheeted && cd.doc?.deletedAt === null;
  const sheets = useCharacterSheets(docId, shown);
  if (!shown || !sheets.data) return null;
  const primary = sheets.data.find((s) => s.isPrimary) ?? sheets.data[0];
  if (!primary && cd.type?.key !== 'pc') return null;
  return (
    <Link
      to="/sheets/pc/$docId"
      params={{ docId }}
      className="cx-button cx-button--secondary cx-button--sm"
      data-testid="sheets.pc.pill"
    >
      <span className="cx-button__label">
        {primary ? `Character sheet · Level ${characterLevel(primary.data)}` : 'Character sheet'}
      </span>
    </Link>
  );
}
